import type { GroupId } from 'types/group'
import type { UINotification } from 'types/notification'
import type { User } from 'types/user'

/**
 * Gets the notification counter timestamp for a specific group.
 *
 * Retrieves the timestamp that marks when the user last checked notifications
 * for the given group. Used to determine which notifications are "new" since
 * the last check.
 *
 * @param user - User object containing notification settings
 * @param groupId - ID of the group to get timestamp for
 * @returns Timestamp string or null if not set
 *
 * @example
 * ```typescript
 * const timestamp = getGroupNotificationCounterTimestamp(user, 'group-123')
 * // Returns '2024-01-15T10:30:00Z' or null
 * ```
 */
export const getGroupNotificationCounterTimestamp = (
  user: User,
  groupId: GroupId,
): string | null | undefined =>
  user.settings?.inbox_counter_timestamps?.[groupId] || null

/**
 * Filters unread notifications for a specific group.
 *
 * Returns notifications that belong to the specified group and are newer
 * than the user's last check timestamp for that group.
 *
 * @param user - User object containing notification settings
 * @param unreadNotifications - Array of all unread notifications
 * @param groupId - ID of the group to filter notifications for
 * @returns Array of unread notifications for the group
 *
 * @example
 * ```typescript
 * const groupNotifications = getUnreadNotificationsForGroup(
 *   user,
 *   allUnreadNotifications,
 *   'group-123'
 * )
 * // Returns notifications for group-123 that are newer than last check
 * ```
 */
export const getUnreadNotificationsForGroup = (
  user: User,
  unreadNotifications: any[],
  groupId: GroupId,
): any[] => {
  const counterTimestamp = getGroupNotificationCounterTimestamp(user, groupId)
  return unreadNotifications.filter(
    notification =>
      notification.group_id === groupId &&
      (!counterTimestamp ||
        new Date(notification.timestamp) > new Date(counterTimestamp)),
  )
}

/**
 * Gets the count of unread messages for a specific group.
 *
 * Calculates how many unread notifications exist for a group since the user's
 * last check. Returns null if there are no unread notifications.
 *
 * @param user - User object containing notification settings
 * @param unreadNotifications - Array of all unread notifications
 * @param groupId - ID of the group to count notifications for
 * @returns Number of unread notifications or null if none
 *
 * @example
 * ```typescript
 * const count = getUnreadMessageCounter(user, notifications, 'group-123')
 * // Returns 5 (if 5 unread) or null (if none)
 *
 * // Use for badge display
 * {count && <Badge count={count} />}
 * ```
 */
export const getUnreadMessageCounter = (
  user: User,
  unreadNotifications: any[],
  groupId: GroupId,
): number | null | undefined => {
  const unreadInboxNotifications = getUnreadNotificationsForGroup(
    user,
    unreadNotifications,
    groupId,
  )
  if (unreadInboxNotifications.length === 0) return null
  return unreadInboxNotifications.length
}

let notificationSystemRef:
  | {
      addNotification: (notification: any) => unknown
    }
  | null
  | undefined = null

/**
 * Sets the reference to the notification system component.
 *
 * Stores a reference to the notification system so that other parts of the
 * application can trigger notifications programmatically.
 *
 * @param ref - Reference to the notification system component
 *
 * @example
 * ```typescript
 * // In notification system component
 * useEffect(() => {
 *   setNotificationSystemRef(notificationSystemRef.current)
 * }, [])
 * ```
 */
export const setNotificationSystemRef = (ref: any) => {
  notificationSystemRef = ref
}

/**
 * Displays a UI notification to the user.
 *
 * Adds a notification to the notification system if it has been initialized.
 * Supports various notification types including success, error, warning, etc.
 *
 * @param notification - Notification object with message, level, and options
 *
 * @example
 * ```typescript
 * pushUINotification({
 *   message: 'File uploaded successfully!',
 *   level: 'success',
 *   dismissible: 'click',
 *   autoDismiss: 3
 * })
 *
 * pushUINotification({
 *   message: 'Upload failed',
 *   level: 'error',
 *   dismissible: 'click'
 * })
 * ```
 */
export const pushUINotification = (notification: UINotification): void => {
  if (notificationSystemRef) {
    notificationSystemRef.addNotification(notification)
  }
}

let notificationId = 0

/**
 * Starts a pending notification and returns a function to update it.
 *
 * Creates a persistent notification with a spinner for long-running operations.
 * Returns a function that can be called to update the notification with
 * final status (success/error) when the operation completes.
 *
 * @param message - Initial message to display with spinner
 * @returns Function to call when operation completes to update the notification
 *
 * @example
 * ```typescript
 * const updateNotification = startPendingUINotification('Uploading file...')
 *
 * try {
 *   await uploadFile()
 *   updateNotification({
 *     message: 'File uploaded successfully!',
 *     level: 'success',
 *     dismissible: 'click'
 *   })
 * } catch (error) {
 *   updateNotification({
 *     message: 'Upload failed',
 *     level: 'error',
 *     dismissible: 'click'
 *   })
 * }
 * ```
 */
export const startPendingUINotification = (
  message: string,
): typeof pushUINotification => {
  const uid = `pending-${notificationId++}`
  pushUINotification({
    uid,
    message,
    level: 'success',
    dismissible: 'none',
    spinner: true,
  })
  return (notification: UINotification) =>
    pushUINotification({
      ...notification,
      _replaceNotificationWithUID: uid,
    })
}
